import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { RayForm, RayFormValidation } from '../../components/common';
import { SERVICE_STATUS, SERVICE_STATUS_TYPE } from '../../config/ServiceConstants';
import { history } from '../../history';
import { ServiceAction, AdminUserAction, ErrorCodeAction, AlertAction } from '../../actions';
import ServiceClaimCategory from './ServiceClaimCategory';
import ServiceInfoAttachments from './ServiceInfoAttachments';
import { InputBasicCheckbox, InputBasicDateTime, InputSelect2Search } from '../common/Inputs';
import { Translate as t } from '../../config/Translate';
import { normalizeErrorCode } from '../../lib/serviceErrorCode';

// 서버로 보내거나 미리 채울 값에서 undefined·null·'' 키를 뺀다 (비운 효력일이 빈 문자열로 가지 않게).
const compact = (obj) => {
	const out = {};
	Object.keys(obj || {}).forEach((k) => {
		const v = obj[k];
		if (v !== undefined && v !== null && v !== '') out[k] = v;
	});
	return out;
};

// 서비스 티켓 만들기 공용 양식. 들어온 경로는 prefill(미리 채울 값)과 product(제품 정보)만 넘긴다.
const ServiceCreateForm = ({ service, apierrors, errorCodes, ErrorCodeList, NotifyError, NotifySuccess, callbackCreated,
	prefill, product, withBack = false, onCancel }) => {
	const [isAddProcess, setIsAddProcess] = useState(false);
	const [newService, setNewService] = useState(() => compact(prefill));
	const [errors, setErrors] = useState([]);
	const [nService, setNService] = useState({});
	const [errorCodeList, setErrorCodeList] = useState([]);
	const [supportRequest, setSupportRequest] = useState(false);
	const [uploadList, setUploadList] = useState([]);

	const prefillKey = JSON.stringify(compact(prefill));

	useEffect(() => {
		setNewService(prev => ({ ...prev, ...compact(prefill) }));
	}, [prefillKey]);

	// 첨부가 있으면 생성 → 업로드 → ServiceAction.Attachment 의 SERVICE_UPDATE_INFO 로 redux service 가 바뀐 뒤 이동
	useEffect(() => {
		if (isAddProcess && service) {
			if (service.ticketKey) {
				callbackCreated && callbackCreated(service);
			} else {
				setIsAddProcess(false);
				callbackCreated && callbackCreated(service);
			}
		}
	}, [service]);

	useEffect(() => {
		if (isAddProcess && uploadList.length == 0 && nService) {
			if (nService.ticketKey) {
				callbackCreated && callbackCreated(nService);
			} else {
				setIsAddProcess(false);
				callbackCreated && callbackCreated(nService);
			}
		}
	}, [nService]);

	useEffect(() => {
		setErrors(apierrors);
	}, [apierrors]);

	useEffect(() => {
		ErrorCodeList && ErrorCodeList();
	}, []);

	useEffect(() => {
		const result = [];
		errorCodes?.forEach(item => {
			if (item.model?.includes(product?.model) && item.lang == 'eng')
				result.push({ code: item.errorCode, title: item.errorCode });
		});
		result.sort((a, b) => a.title.localeCompare(b.title, undefined, { numeric: true }));
		setErrorCodeList([{ code: '-', title: 'None' }, ...result]);
	}, [errorCodes, product]);

	const handleChangeData = (e) => {
		const { name, value } = e.target;

		if (name == 'supportRequestToggle') {
			if (!value) {
				setNewService(prev => {
					const next = { ...prev };
					delete next['supportRequest'];
					delete next['status'];
					delete next['statusType'];
					return next;
				});
			}
		} else if (name == 'supportRequest') {
			setNewService(prev => ({ ...prev, supportRequest: value, status: SERVICE_STATUS.REMOTE, statusType: SERVICE_STATUS_TYPE.OPEN }));
		} else if (name == 'errorCode') {
			setNewService(prev => {
				const next = { ...prev, errorCode: normalizeErrorCode(value) };
				// 미리 채운 오류와 다른 코드를 고르면 그 오류 행과의 연결은 끊는다
				if (next.errorCode !== (prefill?.errorCode ?? '')) delete next['productErrorCodeId'];
				return next;
			});
		} else {
			// BUG-090 후속: functional updater 로 stale closure 방지. parent/detail 연속 변경 시
			// 이전 setNewService 의 결과가 closure 의 newService 보다 최신일 수 있음.
			setNewService(prev => ({ ...prev, [name]: value }));
		}
	};

	const checkSupportRequest = (e) => {
		setSupportRequest(!supportRequest);
		handleChangeData(e);
	};

	const claimCategory = (info) => {
		setNewService(prev => ({ ...prev, category: info?.category, categoryDetail: info?.categoryDetail }));
	};

	const form_controls = [
		{
			title: '', items: [
				{ type: 'input', name: 'summary', label: t('common.field.subject'), isEditable: true, val: newService.summary, size: 12, required: true },
				{ type: 'datetime', name: 'effectiveDate', label: t('common.field.effectiveDate'), isEditable: true, val: newService.effectiveDate, size: 12 },
				{
					type: 'combo', name: 'assignee', label: t('common.label.assignee'), val: newService.assignee,
					startval: 'Unassigned',
					viewLinkPath: '/Admin/AdminUserInfo/',
					// IMP-281: TODO 티켓 대상 회사 기준 확인 필요 (product/prefill.siteId 중 어느 companyId 사용할지 불명확 — 현행 세션 회사 유지)
					// IMP-340: assignee 콤보 이름(title)순 정렬. (Inputs.js 는 getCombo 를 인자 없이 호출 →
					// GetComboListByCompanyId 가 세션 회사로 폴백; ...args 는 향후 인자 전달 대비 무해 forward)
					getCombo: async (...args) => [...(await AdminUserAction.GetComboListByCompanyId(...args))].sort((a, b) => (a.title || '').localeCompare(b.title || '')), isEditable: true, size: 12
				},
				{ type: 'textarea', name: 'description', label: t('common.field.description'), isEditable: true, val: newService.description, size: 12 },
			]
		}
	];

	// 미리 채운 오류코드가 제품 모델 목록에 없어도 선택값으로 보이게 한다
	const selectList = (newService.errorCode && !errorCodeList.some(x => x.code == newService.errorCode))
		? [...errorCodeList, { code: newService.errorCode, title: newService.errorCode }]
		: errorCodeList;

	const validAdd = () => {
		// 재진입 가드 — 더블클릭 시 티켓 2건 생성 차단
		if (isAddProcess) return;
		const errValidate = RayFormValidation(form_controls, newService);

		if (errValidate.length > 0) {
			setErrors(errValidate);
		} else {
			setIsAddProcess(true);
			createService(compact(newService));
		}
	};

	const createService = async (data) => {
		const created = await ServiceAction.CreateServiceLocal(data);
		if (!created) {
			// BUG-225 (#443): 실패 시 해제 이펙트(nService 조건)가 skip 되어 처리 중이 고정되던 것을
			// 해제하고 실패를 사용자에게 알린다.
			setIsAddProcess(false);
			if (NotifyError) {
				NotifyError(t('service.message.createFailed'));
			} else {
				// NotifyError 미주입 시 무음 실패 퇴행 방지 — 생략 사실을 관측 가능하게 남긴다
				console.warn('[ServiceCreateForm] NotifyError 미주입 — 사용자 안내 생략', { reason: '서비스 생성 실패 토스트(createFailed) 미표시 (BUG-225)' });
			}
			return;
		}
		// 성공 안내는 다음 await 전에 — meta.dropped 안내가 마지막 알림으로 남도록(axiosSetup)
		if (NotifySuccess) NotifySuccess(t('common.message.success.saved'));
		setNService(created);
	};

	return (
		<>
			<div className="card me-1 ms-1 mt-1 section">
				<div className="item p-2">
					<InputSelect2Search
						options={{
							type: "select2",
							name: "errorCode",
							label: <div>{t('product.field.errorCode')} {errorCodeList.length > 1 && <span className='small text-muted'>({errorCodeList.length - 1})</span>}</div>,
							list: selectList,
							val: newService.errorCode || null,
							required: false
						}}
						editable={true}
						onchange={handleChangeData} />
				</div>
			</div>

			<RayForm controls={form_controls} isForm={true} showAllFields={true} handleChangeData={handleChangeData} errors={errors} />

			<div className="card me-1 ms-1 mt-1">
				<ServiceClaimCategory service={nService} pInfo={product} callback={claimCategory} />
			</div>

			<div className="card me-1 ms-1 mt-1">
				<ServiceInfoAttachments propService={nService} uploadLater={true} callback={(list) => setUploadList(list)} />
			</div>

			<div className="card me-1 ms-1 mt-1">
				<div>
					<div className="card-header">
						{t('service.label.supportRequest')}
						<div className='float-end'>
							<InputBasicCheckbox options={{ name: 'supportRequestToggle', val: supportRequest }} editable={true} onchange={checkSupportRequest} />
						</div>
					</div>

					{supportRequest && <div className='card-body pb-1 pt-1'>
						<InputBasicDateTime
							options={{ name: 'supportRequest', label: '' }}
							editable={true}
							onchange={handleChangeData}
						/>
					</div>}
				</div>
			</div>

			<div className="section">
				<button className="btn btn-primary btn-block" onClick={() => validAdd()} disabled={isAddProcess}> {isAddProcess ? t('common.status.processing') || '...' : t('common.button.submit')} </button>
				{withBack && <button className="btn btn-secondary btn-block" onClick={() => history.goBack()}> {t('common.button.back')} </button>}
				{onCancel && <button className="btn btn-secondary btn-block" onClick={() => onCancel()}> {t('common.button.cancel')} </button>}
			</div>
		</>
	);
};

const mapState = (state) => {
	const service = state.ServiceReducer.service;
	const apierrors = state.AlertReducer.errors;
	const errorCodes = state.ErrorCodeReducer.errorCodes;
	return { apierrors, service, errorCodes };
};

const mapDispatch = (dispatch) => ({
	ErrorCodeList: () => dispatch(ErrorCodeAction.GetList()),
	// BUG-225: 등록 실패 토스트 (CreateServiceLocal 은 비-redux 라 컴포넌트에서 dispatch)
	NotifyError: (message) => dispatch(AlertAction.ErrorMsg(message)),
	NotifySuccess: (message) => dispatch(AlertAction.Success(message)),
});

export default connect(mapState, mapDispatch)(ServiceCreateForm);
